/**
 * Which theme the app is wearing, and remembering the choice.
 *
 * Light is the default. That is the design system's direction — Paper task
 * surfaces on a Canvas background, with the violet rail as the one saturated
 * element — and `prefers-color-scheme` is deliberately *not* consulted: a
 * default plus an explicit choice is the whole rule.
 *
 * The installed launch screen is not part of that choice. `theme_color` can
 * name exactly one colour and the OS draws the launch before any script reads
 * the stored theme, so the manifest names the dark canvas as the brand's launch
 * colour on every device (g50). The page that follows is blank in the saved
 * theme, so the handoff from the launch is dark to light or dark to dark, and
 * never a light launch into a dark app.
 *
 * The choice lives in `localStorage`, which is the right store for it: it is a
 * per-device preference, not account data, and a second person signing in on
 * this device inheriting the theme is harmless in a way that inheriting a list
 * would not be.
 */

export type Theme = "light" | "dark";

export const THEME_KEY = "trayflux.theme";

/**
 * The colour the browser paints its own chrome with — the address bar on
 * Android, the status bar area of an installed window.
 *
 * These are `--canvas` in each theme and must stay equal to it. The manifest's
 * `theme_color` names the dark one, because every installed launch is dark.
 * The meta tag in `index.html` defaults to the light one, because light is what
 * a first visit paints, and it is what moves afterwards.
 */
const CHROME: Record<Theme, string> = {
  light: "#F6F7FB",
  dark: "#161621",
};

function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark";
}

/**
 * Reads the stored choice, or `undefined` when there is none.
 *
 * Wrapped because `localStorage` is not merely empty in a private window or
 * with site data blocked — the accessor itself throws. A theme is the last
 * thing that should be able to stop the app rendering.
 */
export function storedTheme(): Theme | undefined {
  try {
    const value = localStorage.getItem(THEME_KEY);
    return isTheme(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

export function currentTheme(): Theme {
  return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
}

/**
 * Applies a theme to the document and to the browser's chrome.
 *
 * `light` removes the attribute rather than setting `data-theme="light"`, so
 * the default state of the document is the default state of the stylesheet —
 * one less way for the two to disagree.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === "dark") root.dataset.theme = "dark";
  else delete root.dataset.theme;

  // The design system requires this explicitly: if the theme colour is switched
  // at runtime, the meta tag moves with it. Without this the chrome keeps the
  // manifest's light colour while the app goes dark, which reads as a rendering
  // fault rather than a choice.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = CHROME[theme];
}

/**
 * Who to tell when the theme moves.
 *
 * The shell carries two switches — one in the rail, one in the phone top bar —
 * because a single node cannot be in both, and a switch that still reads
 * "off" after the other one turned the lights on is a control lying about the
 * state of the app. They are separate buttons kept in step by this.
 */
const watchers = new Set<(theme: Theme) => void>();

export function onTheme(fn: (theme: Theme) => void): () => void {
  watchers.add(fn);
  return () => watchers.delete(fn);
}

export function setTheme(theme: Theme): void {
  applyTheme(theme);
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // The theme still applied; it just will not survive a reload. Failing the
    // interaction over that would be the worse trade.
  }
  for (const watcher of watchers) watcher(theme);
}

export function toggleTheme(): Theme {
  const next: Theme = currentTheme() === "dark" ? "light" : "dark";
  setTheme(next);
  return next;
}
